import React from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { FixedSizeList } from "react-window";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => ({
  endAdornment: {
    top: ({ hasLabel }) => (hasLabel ? 9 : "unset")
  }
}));

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = ({ itemSize }) =>
  React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;

    const getHeight = () => {
      return (itemCount > 8 ? 8 : itemCount) * itemSize;
    };

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={itemData}
            height={getHeight() + LISTBOX_PADDING}
            width="100%"
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={itemSize}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

ListboxComponent.propTypes = {
  children: PropTypes.node
};

export default function SearchInput({
  items,
  renderOption,
  getOptionLabel,
  label,
  placeholder,
  filterOptions,
  itemSize,
  onChange,
  value,
  loading,
  helperText,
  InputProps,
  getOptionSelected,
  disabled,
  classes,
  disablePortal,
  open,
  multiple,
  error,
  ...other
}) {
  const styles = useStyles({ hasLabel: !!label });

  return (
    <Autocomplete
      disableListWrap
      ListboxComponent={ListboxComponent({ itemSize })}
      options={items}
      filterOptions={filterOptions}
      renderInput={params => {
        return (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              "data-qa": "search-input-autocomplete"
            }}
            variant="filled"
            label={label}
            hiddenLabel={!label}
            placeholder={placeholder}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              disableUnderline: true,
              hiddenLabel: !label
            }}
            error={error}
            disabled={disabled}
          />
        );
      }}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      onChange={onChange}
      loading={loading}
      value={value}
      isOptionEqualToValue={getOptionSelected}
      disabled={disabled}
      classes={{ endAdornment: styles.endAdornment, ...classes }}
      disablePortal={disablePortal}
      multiple={multiple}
      hiddenLabel={!label}
      open={open}
      {...other}
    />
  );
}

SearchInput.propTypes = {
  items: PropTypes.array,
  renderOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  filterOptions: PropTypes.func,
  itemSize: PropTypes.number,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.any,
  getOptionSelected: PropTypes.func,
  disabled: PropTypes.bool
};
